/**
 * src/lib/ceoReportJob.js — the CEO report generation, held OUTSIDE the dialog.
 *
 * Card P219 item 8: "Closing the dialog doesn't cancel; reopening shows the
 * finished report or the working panel."
 *
 * ⛔⛔ THAT SENTENCE IS AN ARCHITECTURE DECISION, NOT A UI TWEAK. The request
 * used to live in AiPipelineReport's own state, so closing the dialog
 * unmounted the component and threw the in-flight promise away — the call
 * carried on at the server's expense and its answer had nowhere to land.
 * Reopening started again from nothing, which for a fifty-second call means a
 * reader who closes the dialog to look at a tile pays the fifty seconds twice.
 *
 * So the job lives here, at module scope, and the dialog SUBSCRIBES to it.
 * Mount, unmount and remount as often as you like: the state is the job's,
 * not the component's.
 *
 * ⚠ ONE JOB AT A TIME, ON PURPOSE. A second start while one is running is
 * ignored rather than queued — the card asks for exactly one request per
 * click, and a queue would turn an impatient double-click into two charges
 * against the model with the second answer overwriting the first.
 *
 * ⚠ Module scope means this survives for the life of the TAB, which is the
 * intent, and resets on reload. Nothing here is persisted: a half-finished
 * report is not worth a storage key, and a finished one is cheaper to
 * regenerate than to invalidate correctly.
 */

/** @typedef {'idle'|'running'|'done'|'error'} JobStatus */

const listeners = new Set();

let state = {
  status: 'idle',
  startedAt: null,
  finishedAt: null,
  /** The scope the running job was started for — echoed in the panel. */
  scope: null,
  /** The server's answer, verbatim, once it arrives. */
  result: null,
  error: null,
  /** Bumped per start; a stale answer carrying an older id is discarded. */
  id: 0,
};

function emit() {
  const snapshot = state;
  for (const fn of [...listeners]) {
    try { fn(snapshot); } catch (_) { /* a bad subscriber must not stop the rest */ }
  }
}

export function getJob() { return state; }

/** Subscribe; returns an unsubscribe. The callback fires immediately. */
export function subscribeJob(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  fn(state);
  return () => { listeners.delete(fn); };
}

/**
 * Start a generation, unless one is already running.
 *
 * @param {Function} run   async () => serverResult
 * @param {object}   scope { pipelineLabel, rangeLabel } for the working panel
 * @returns {boolean} whether a job was actually started
 */
export function startJob(run, scope = null) {
  if (state.status === 'running') return false;
  const id = state.id + 1;
  state = { status: 'running', startedAt: Date.now(), finishedAt: null,
            scope, result: null, error: null, id };
  emit();

  /*
   * ⛔⛔ run() IS CALLED SYNCHRONOUSLY, NOT VIA Promise.resolve().then(run).
   * The deferred form starts the network call a microtask after the click,
   * which buys nothing and makes "clicking sent a request" untrue for one
   * tick — a test asserting it right after the click saw ZERO calls, and so
   * would any other synchronous observer. The try/catch keeps a run() that
   * throws synchronously on the same path as one that rejects.
   */
  let work;
  try {
    work = Promise.resolve(run());
  } catch (err) {
    work = Promise.reject(err);
  }

  work
    .then((result) => {
      /* ⛔ A superseded answer is dropped. resetJob() bumps the id too, so a
         job the reader explicitly cleared cannot reappear later. */
      if (id !== state.id) return;
      state = { ...state, status: 'done', finishedAt: Date.now(), result, error: null };
      emit();
    })
    .catch((err) => {
      if (id !== state.id) return;
      state = { ...state, status: 'error', finishedAt: Date.now(), result: null,
                error: (err && err.message) || 'The report could not be generated.' };
      emit();
    });
  return true;
}

/**
 * Clear the job.
 *
 * ⚠ BUMPS THE ID, so an in-flight call that resolves afterwards is ignored
 * rather than re-populating a panel the reader has dismissed.
 */
export function resetJob() {
  state = { status: 'idle', startedAt: null, finishedAt: null, scope: null,
            result: null, error: null, id: state.id + 1 };
  emit();
}

/** Test seam: drop every subscriber and return to idle. */
export function __resetForTests() {
  listeners.clear();
  state = { status: 'idle', startedAt: null, finishedAt: null, scope: null,
            result: null, error: null, id: 0 };
}
